import { Card } from '../../Card'
import { CardHeader } from '../../CardHeader'
import RecordAudioIcon from '@/CustomIcons/RecordAudioIcon'
import { Tutorial } from '../../Tutorial'
import Tooltip from '@/UIComponents2/Tooltip'
import FolderIcon from '@/CustomIcons/FolderIcon'
import { ControlButton } from '@/UIComponents2/ControlButton'
import { Button } from '@/UIComponents2/Button'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import { env } from '@/services/EnvironmentVariables'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { useAppContext } from '@/AppContext'
import ElectronIPC from '@/services/ElectronIPC'
import useAppSettingsActions from '@/actions/useAppSettingsActions'
import { queryCurrentMediaSelectionWithFallback } from '@/Logic'
import { useUserContext } from '@/UserContext'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import { useTranslation } from 'react-i18next'
import recordingCoverJpg from '@/assets/tutorials/recording-cover.jpg'

type ScreenRecordingSectionProps = {
    inSession: boolean
}
export function AudioRecordingSection(props: ScreenRecordingSectionProps) {
    const [appState] = useAppContext()
    const { updateAppSettingItem } = useAppSettingsActions()

    const inBrowser = !env.IS_ELECTRON

    return (
        <Card className="md:p-[10px] flex flex-col gap-[10px]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
                <div className="flex flex-col pl-[20px] py-[15px] gap-7">
                    <div className="flex justify-between">
                        <CardHeader title="Audio Recording" icon={RecordAudioIcon} />
                    </div>
                    {env.OS === 'win32' && (
                        <div>
                            <WindowsRecordingDeviceSelect />
                        </div>
                    )}
                    <div className="grid grid-cols-[1fr_min-content] gap-2">
                        <Tooltip text={appState.appSettings.remoteRecordSaveDirectory}>
                            <Button
                                variant="rectangle"
                                className="border-muse-toplit truncate"
                                disabled={inBrowser}
                                onClick={async () => {
                                    const r = await ElectronIPC.openFileDialog({
                                        properties: ['openDirectory'],
                                    })
                                    const selectedDirectoryPath = (r?.paths || [])[0] || null
                                    if (selectedDirectoryPath) {
                                        updateAppSettingItem(
                                            'remoteRecordSaveDirectory',
                                            selectedDirectoryPath,
                                            'string'
                                        )
                                    }
                                }}
                            >
                                <span className="truncate">
                                    {appState.appSettings.remoteRecordSaveDirectory || '(none)'}
                                </span>
                            </Button>
                        </Tooltip>
                        <Tooltip text="Browse recording folder">
                            <ControlButton
                                variant="secondary"
                                disabled={inBrowser || !appState.appSettings.remoteRecordSaveDirectory}
                                className="shadow-glow"
                                onClick={() => {
                                    ElectronIPC.openPath(appState.appSettings.remoteRecordSaveDirectory)
                                }}
                            >
                                <FolderIcon style={{ fontSize: '1.3rem' }} />
                            </ControlButton>
                        </Tooltip>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div>Save recording to file</div>
                        <Checkbox
                            label="When you record someone else"
                            checked={appState.appSettings.recorderSaveRemoteRecording}
                            onChange={(val) => {
                                updateAppSettingItem('recorderSaveRemoteRecording', val)
                            }}
                        />
                        <Checkbox
                            label="When someone records you"
                            checked={appState.appSettings.recordeeSaveLocalRecording}
                            onChange={(val) => {
                                updateAppSettingItem('recordeeSaveLocalRecording', val)
                            }}
                        />
                        <Checkbox
                            label="Automatically upload files to chat"
                            disabled={true}
                            onChange={(val) => {
                                // TODO: Not implemented
                            }}
                        />
                    </div>
                </div>
                <div>
                    <Tutorial
                        coverImageUrl={recordingCoverJpg}
                        title="Simple Recording"
                        source="https://museapp.sfo3.cdn.digitaloceanspaces.com/in-app-tutorials/recording-mac.mp4"
                        sourceType="video/mp4"
                    />
                </div>
            </div>
        </Card>
    )
}

function WindowsRecordingDeviceSelect() {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const { setMediaSelection } = useMediaSelectionActions()

    const value = queryCurrentMediaSelectionWithFallback(
        userState.mediaSelection,
        userState.mediaSources,
        'audioOutputMuseRecording'
    )

    const items = () => {
        let items = [
            { value: '_undef', label: t('Select device') },
            { value: '_none', label: t('None') },
        ]

        for (const source of userState.mediaSources.audioOutputsAll) {
            items.push({
                value: source.deviceId,
                label: source.label,
            })
        }

        return items
    }

    return (
        <SimpleSelect
            triggerClassName="py-[12px]"
            label="Select Recording Device"
            items={items()}
            value={value}
            onChange={(val) => {
                if (val) {
                    setMediaSelection({
                        audioOutputMuseRecording: val,
                    })
                }
            }}
            widthMatching
        />
    )
}
